import { useState, useEffect, useMemo } from 'react'
import { Zap, Loader2, Search, DollarSign, Smartphone, ChevronLeft, Info, Printer } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs'
import { Badge } from '@/app/components/ui/badge'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import { SearchFilter } from '@/app/components/ui/SearchFilter'
import { toast } from 'sonner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { wbsService, WbsProduct, WbsCarrierUsage, WbsTransaction, wbsSaleAmounts } from '@/app/services/wbs.service'
import { useDebounce } from '@/hooks/useDebounce'
import { TicketPreviewModal } from './TicketPreviewModal'
import {
  WbsTransactionLookupErrorCard,
  WbsTransactionResultCard,
} from './WbsTransactionResultCard'

// ── Logos de operadores via Google Favicons API ──────────────────────────────
const CARRIER_DOMAIN_MAP: Record<string, { url: string, requireContrast?: boolean }> = {
  // ---------Recargas / Telefonía---------
  telcel: { url: 'telcel.com', requireContrast: false },
  'paquete amigo': { url: 'telcel.com', requireContrast: false },
  'internet amigo': { url: 'telcel.com', requireContrast: false },
  att: { url: 'att.com.mx', requireContrast: true },
  'at&t': { url: 'att.com.mx', requireContrast: true },
  alo: { url: 'hablamexico.com/', requireContrast: false },
  axios: { url: 'axiosmobile.mx/', requireContrast: false },
  movistar: { url: 'movistar.com.mx', requireContrast: false },
  bait: { url: 'mibait.com/', requireContrast: false },
  bigcel: { url: 'bigcel.mx/', requireContrast: true },
  unefon: { url: 'unefon.com.mx', requireContrast: false },
  virgin: { url: 'virginmobile.mx', requireContrast: false },
  //flash: { url: 'flashmobile.com.mx', requireContrast: false }, // Sin Favicon en Google
  maz: { url: 'mazmovil.com', requireContrast: false },
  //contigo: { url: 'contigomobile.com.mx', requireContrast: false }, // Sin Favicon en Google
  oui: { url: 'ouimovil.com.mx', requireContrast: false },
  diri: { url: 'mimovil.com.mx', requireContrast: false },
  'mi movil': { url: 'mimovil.com.mx', requireContrast: false },
  freedom: { url: 'freedompop.mx', requireContrast: false },
  guga: { url: 'queplan.mx', requireContrast: false },
  inphonity: { url: 'inphonity.com', requireContrast: false },
  bienestar: { url: 'internetparaelbienestar.mx', requireContrast: false },
  jrmovil: { url: 'jrmovil.com', requireContrast: false },
  mobig: { url: 'mobig.mx', requireContrast: false },
  pillofon: { url: 'pillofon.mx', requireContrast: false },
  rincel: { url: 'rincel.com.mx', requireContrast: false },
  soriana: { url: 'sorianamovil.com.mx', requireContrast: false },
  ultracel: { url: 'ultracel.com.mx', requireContrast: false },
  weex: { url: 'weex.mx', requireContrast: false },
  wimo: { url: 'wimotelecom.com', requireContrast: false },
  //simpati: { url: 'simpati.mobi', requireContrast: false }, Parece que no tiene sitio web real
  //macropay: { url: 'macropay.mx', requireContrast: false }, // Sin Favicon en Google
  //recargauniversla: { url: 'macropay.mx', requireContrast: false }, // Parece ser global
  //iusacell: { url: 'iusacell.com.mx', requireContrast: false }, // Comprada por AT&T, Ya no existe
  //nextel: { url: 'nextel.com.mx', requireContrast: false }, // Comprada por AT&T, Ya no existe


  // -------Servicios--------
  telmex: { url: 'telmex.com', requireContrast: false },
  totalplay: { url: 'totalplay.com.mx', requireContrast: false },
  izzi: { url: 'izzi.mx', requireContrast: false },
  megacable: { url: 'megacable.com.mx', requireContrast: false },
  cfe: { url: 'cfe.mx', requireContrast: false },
  axtel: { url: 'axtel.com', requireContrast: false },
  dish: { url: 'dish.com.mx', requireContrast: false },
  netflix: { url: 'netflix.com', requireContrast: false },
  spotify: { url: 'spotify.com', requireContrast: false },
  amazon: { url: 'amazon.com.mx', requireContrast: false },
  xbox: { url: 'xbox.com', requireContrast: false },
  nintendo: { url: 'nintendo.com', requireContrast: false },
  google: { url: 'google.com', requireContrast: false },
  apple: { url: 'apple.com', requireContrast: false },
  playstation: { url: 'playstation.com', requireContrast: false },
  uber: { url: 'uber.com', requireContrast: false },
  rappi: { url: 'rappi.com.mx', requireContrast: false },
  didi: { url: 'didiglobal.com', requireContrast: false },
  'elektra': { url: 'elektra.mx', requireContrast: false },
  'agua de colima': { url: 'ciapacov.gob.mx', requireContrast: false },
  'agua de irapuato': { url: 'japami.gob.mx', requireContrast: false },
  'agua de merida': { url: 'japay.yucatan.gob.mx', requireContrast: false },
  'agua de mexicali': { url: 'cespm.gob.mx', requireContrast: false },
  'agua de tijuana': { url: 'cespt.gob.mx', requireContrast: false },
  'agua de torreon': { url: 'simastorreon.gob.mx', requireContrast: true },
  'agua progreso': { url: 'smapap.gob.mx', requireContrast: false },
  'aguas de durango': { url: 'amd.gob.mx', requireContrast: false },
  'estado de mexico': { url: 'edomex.gob.mx', requireContrast: false },
  'estado de michoacan': { url: 'michoacan.gob.mx', requireContrast: false },
  aguakan: { url: 'aguakan.com', requireContrast: false },
  arabela: { url: 'arabela.com', requireContrast: false }, // Sin Favicon
  avon: { url: 'avon.mx', requireContrast: false },
  belcorp: { url: 'uneteabelcorp.com/mx', requireContrast: false },
  blue: { url: 'bluetelecomm.mx', requireContrast: false },
  andrea: { url: 'mx.andrea.com', requireContrast: false },
  aras: { url: 'ara.com.mx', requireContrast: false },
  cmapas: { url: 'cmapas.gob.mx', requireContrast: false },
  fuller: { url: 'fullercosmetics.com', requireContrast: false },
  'gas natural': { url: 'naturgy.com.mx', requireContrast: false },
  'gobierno coahuila': { url: 'coahuila.gob.mx', requireContrast: false },
  'gobierno gto': { url: 'guanajuato.gob.mx', requireContrast: false },
  'gobierno hidalgo': { url: 'hidalgo.gob.mx', requireContrast: true },
  'gobierno jalisco': { url: 'jalisco.gob.mx', requireContrast: false },
  'gobierno tabasco': { url: 'tabasco.gob.mx', requireContrast: false },
  'gobierno tlaxcala': { url: 'tlaxcala.gob.mx', requireContrast: false },
  'municipio celaya': { url: 'celaya.gob.mx', requireContrast: false },
  'municipio naucalpan': { url: 'naucalpan.gob.mx', requireContrast: false },
  //'municipio reynosa': { url: 'reynosa.gob.mx', requireContrast: false }, // Sin favicon
  herbalife: { url: 'myherbalife.com', requireContrast: false },
  ilusion: { url: 'ilusion.com', requireContrast: false },
  interapas: { url: 'pagos.interapas.mx', requireContrast: false },
  jafra: { url: 'jafra.com.mx', requireContrast: false },
  lebel: { url: 'uneteabelcorp.com', requireContrast: false },
  mueve: { url: 'mueveciudad.com', requireContrast: false },
  natura: { url: 'natura.com.mx', requireContrast: false },
  seapal: { url: 'seapal.gob.mx', requireContrast: false },
  siapa: { url: 'jalisco.gob.mx', requireContrast: false },
  sideapa: { url: 'sideapa.gob.mx', requireContrast: false },
  sky: { url: 'sky.com.mx', requireContrast: false },
  televia: { url: 'televia.com.mx', requireContrast: false },
  telnor: { url: 'telnor.com', requireContrast: false },
  tupperware: { url: 'tupperware.com.mx', requireContrast: false },
  veolia: { url: 'latinoamerica.veolia.com', requireContrast: false },
  vetv: { url: 'sky.com.mx', requireContrast: false },
  yanbal: { url: 'yanbal.com', requireContrast: false },
  yves: { url: 'yves-rocher.com.mx', requireContrast: false },
  'zermat': { url: 'zermat.com.mx', requireContrast: false },
  'z gas': { url: 'grupozetajalisco.com', requireContrast: false },
  'tesoreria cdmx': { url: 'dgtc.finanzas.cdmx.gob.mx', requireContrast: false },
  'tesoreria df': { url: 'dgtc.finanzas.cdmx.gob.mx', requireContrast: false },
  'super boletos': { url: 'superboletos.com', requireContrast: false },
  'star go': { url: 'stargomexico.com', requireContrast: true },
  'star tv': { url: 'startvmexico.com', requireContrast: false },
  //stanhome: { url: 'stanhome.com.mx', requireContrast: false }, // Sin Favicon
  //soapap: { url: 'soapap.gob.mx', requireContrast: false }, // Sin favicon
  //maxcom: { url: 'maxcom.mx', requireContrast: false }, //Sin Favicon
  //maxigas: { url: 'maxcom.mx', requireContrast: false }, // No tiene sitio web
  //jumapa: { url: 'jumapacelaya.gob.mx', requireContrast: false }, //Sin Favicon
  //jmas: { url: 'jmaschih.gob.mx', requireContrast: false }, //Sin Favicon
  //iave: { url: 'iave.capufe.gob.mx', requireContrast: false }, // Sin favicon
  //ecogas: { url: 'ecogas.com.mx', requireContrast: false }, // Sin Favicon
  //capama: { url: 'capama.gob.mx', requireContrast: false }, // Sin favicon
  //cablemas: { url: 'bluetelecomm.mx', requireContrast: false }, // Se integro a Izzi
  //'agua queretaro': { url: 'appcea.ceaqueretaro.gob.mx/PagoEnLinea/', requireContrast: false }, // Sin Favicon
  //'agua de monterrey': { url: 'sadm.gob.mx', requireContrast: false }, // Sin Favicon
  //'agua de la cdmx': { url: 'aplicaciones.sacmex.cdmx.gob.mx', requireContrast: false }, // Sin Favicon
  //'agua de ensenada': { url: 'cespe.gob.mx', requireContrast: false }, // Sin favicon
}

function getCarrierDomain(carrierId: string, description: string): string | null {
  const haystack = (carrierId + ' ' + description).toLowerCase()
  for (const [key, value] of Object.entries(CARRIER_DOMAIN_MAP)) {
    console.log(description)
    if (haystack.includes(key)) return typeof value === 'string' ? value : value.url
  }
  return null
}

function getCarrierRequireContrast(carrierId: string, description: string) {
  const haystack = (carrierId + ' ' + description).toLowerCase()
  for (const [key, value] of Object.entries(CARRIER_DOMAIN_MAP)) {
    if (haystack.includes(key)) return value.requireContrast
  }
}

function CarrierLogo({ carrierId, description, size = 28 }: { carrierId: string; description: string; size?: number }) {
  const domain = getCarrierDomain(carrierId, description)
  const requireContrast = getCarrierRequireContrast(carrierId, description) ?? false
  const [hasError, setHasError] = useState(false)
  const px = size <= 32 ? 32 : 64
  //console.log(carrierId, description)

  if (!domain || hasError) {
    const initials = (description || carrierId).trim().slice(0, 2).toUpperCase()
    return (
      <span
        style={{ width: size, height: size }}
        className="flex items-center justify-center rounded-lg bg-orange-100 text-orange-700 font-bold text-xs shrink-0 border border-orange-200"
      >
        {initials}
      </span>
    )
  }
  return (
    <span
      style={{ width: size + 8, height: size + 8 }}
      className={`flex items-center justify-center rounded-lg ${requireContrast ? 'bg-gray-600' : 'bg-white'} border border-slate-200 shadow-sm shrink-0 p-1`}
    >
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=${px}`}
        alt={description}
        width={size}
        height={size}
        className="object-contain"
        onError={() => setHasError(true)}
      />
    </span>
  )
}

// ── Determina si un producto es de tipo servicio ──────────────────────────────
function productIsService(p: WbsProduct): boolean {
  const g = p.group?.toUpperCase() ?? ''
  return g.includes('SERVICIO') || g.includes('PAYMENT') || g.includes('SERVICE')
}

export function canCheckWbsDebt(
  isService: boolean,
  product: WbsProduct | null,
  reference: string,
): boolean {
  return Boolean(isService && product?.check_amount && reference.trim())
}

// ── Agrupa los carriers únicos de una lista de productos ──────────────────────
// usageMap: mapa de carrier_id → número de transacciones exitosas en la sucursal.
// Los carriers con más uso aparecen primero; los no usados se ordenan alfabéticamente al final.
function groupCarriers(
  products: WbsProduct[],
  usageMap: Map<string, number> = new Map()
): Array<{ carrier_id: string; name: string; count: number; usageCount: number }> {
  const map = new Map<string, { name: string; count: number }>()
  for (const p of products) {
    const key = p.carrier_id
    if (!map.has(key)) {
      // El nombre del operador es la parte antes del primer '-' o '(' en la descripción
      const name = p.description.split(/[-(/]/)[0].trim() || p.carrier_id
      map.set(key, { name, count: 0 })
    }
    map.get(key)!.count++
  }
  return Array.from(map.entries())
    .map(([carrier_id, { name, count }]) => ({
      carrier_id,
      name,
      count,
      usageCount: usageMap.get(carrier_id) ?? 0,
    }))
    .sort((a, b) => {
      // 1. Primero los más usados en esta sucursal
      if (b.usageCount !== a.usageCount) return b.usageCount - a.usageCount
      // 2. Desempate alfabético
      return a.name.localeCompare(b.name)
    })
}

interface WbsRechargeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WbsRechargeModal({ open, onOpenChange }: WbsRechargeModalProps) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('recharge')

  // Form states
  const [selectedProduct, setSelectedProduct] = useState<WbsProduct | null>(null)
  const [amount, setAmount] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [folio, setFolio] = useState('')
  const [manualTransactionResult, setManualTransactionResult] = useState<WbsTransaction | null>(null)
  const [manualTransactionError, setManualTransactionError] = useState<{
    title: string
    message: string
  } | null>(null)
  const [balanceDetails, setBalanceDetails] = useState<any>(null)
  const [debtAmountEditable, setDebtAmountEditable] = useState<boolean | null>(null)

  // Polling / Transaction states
  const [txStatus, setTxStatus] = useState<'IDLE' | 'SENDING' | 'PENDING' | 'SUCCESS' | 'FAILED'>('IDLE')
  const [activeFolio, setActiveFolio] = useState<string>('')
  const [pollCount, setPollCount] = useState(0)

  // Debug mode
  const [isDebugMode, setIsDebugMode] = useState(false)

  // Checkout states
  const [showTicketPreview, setShowTicketPreview] = useState(false)
  const [generatedSale, setGeneratedSale] = useState<any>(null)
  const generatedSaleAmounts = wbsSaleAmounts(generatedSale)

  const { data: registerContext, refetch: refetchRegisterContext } = useQuery({
    queryKey: ['register-context'],
    queryFn: () => import('@/app/services/pos.service').then(m => m.posRegisterService.getRegisterContext()),
    staleTime: 0,
    enabled: open
  })
  const sucursalId = registerContext?.sucursal?.id

  const checkoutMutation = useMutation({
    mutationFn: async (data: { folio: string; payment_method_id: number; cash_received_amount?: string }) => {
      const contextResult = await refetchRegisterContext({ throwOnError: true })
      if (contextResult.data?.can_complete_sale === false) {
        throw new Error('Debe asignar un vendedor válido a la sesión antes de completar la venta.')
      }
      if (contextResult.data?.cash_capacity?.can_receive_cash === false) {
        throw new Error('La caja ya supera el límite de efectivo. Realice un retiro antes de registrar otra venta.')
      }

      return wbsService.checkoutTransaction(data.folio, {
        payment_method_id: data.payment_method_id,
        cash_received_amount: data.cash_received_amount
      })
    },
    onSuccess: (res: any, variables) => {
      const sale = res?.data?.sale
      setGeneratedSale(sale)
      setShowTicketPreview(true)
      setTxStatus('IDLE') // Reset main view
      setManualTransactionResult(current => (
        current?.folio_pos === variables.folio && sale?.id
          ? { ...current, sale_id: sale.id }
          : current
      ))
      queryClient.invalidateQueries({ queryKey: ['wbs-transactions'] })
      queryClient.invalidateQueries({ queryKey: ['register-context'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error al generar la venta.')
    }
  })
  // Se oculta el botón de debug para producción
  const showDebugButton = false // ---------- Cambiar a true si se quiere revisar el modo debug de transacciones --------
  const [debugLogs, setDebugLogs] = useState<any[]>([])

  const addDebugLog = (type: string, data: any) => {
    setDebugLogs(prev => [{ time: new Date().toISOString(), type, data }, ...prev].slice(0, 10))
  }

  // History states
  const [hSearch, setHSearch] = useState('')
  const [hPage, setHPage] = useState(1)
  const debouncedHSearch = useDebounce(hSearch, 400)

  // ── 3-step selector state ────────────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState<'RECHARGE' | 'SERVICE' | null>(null)
  const [selectedCarrierId, setSelectedCarrierId] = useState<string | null>(null)
  const [packageSearch, setPackageSearch] = useState('')
  const [carrierSearch, setCarrierSearch] = useState('')

  // Derived state for service check
  const isService = selectedProduct ? productIsService(selectedProduct) : false
  // Make amount readonly only if it's a strict fixed amount and not a service that might need checking
  const isAmountFixed = (selectedProduct !== null && !selectedProduct.check_amount && Number(selectedProduct.amount) > 0 && !isService)
    || (isService && selectedProduct?.check_amount === true && debtAmountEditable === false)

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        handleReset()
        setFolio('')
        setManualTransactionResult(null)
        setManualTransactionError(null)
        setActiveTab('recharge')
      }, 300)
    }
  }, [open])

  // ── Carga única del catálogo completo al abrir el modal ──────────────────────
  const { isFetching: isFetchingProducts, data: allProductsData } = useQuery({
    queryKey: ['wbs-products-all'],
    queryFn: () => wbsService.getAllProducts(),
    enabled: open,
    staleTime: 1000 * 60 * 15,   // 15 min cache — el catálogo cambia poco
    gcTime: 1000 * 60 * 30,
  })

  const { data: checkoutConfig, isError: isCheckoutConfigError } = useQuery({
    queryKey: ['wbs-checkout-config'],
    queryFn: wbsService.getCheckoutConfig,
    enabled: open,
    staleTime: 1000 * 60 * 5,
  })

  const serviceCommission = isService ? Number(checkoutConfig?.service_commission_amount || 0) : 0
  const totalToCharge = Number(amount || 0) + serviceCommission

  // ── Ranking de carriers por uso en esta sucursal ─────────────────────────────
  // staleTime de 5 min: React Query cachea el resultado en memoria. Sin Redis.
  // La query llega al backend solo 1 vez cada 5 min por apertura del modal.
  const { data: carrierUsageData } = useQuery({
    queryKey: ['wbs-carrier-usage', sucursalId],
    queryFn: () => wbsService.getCarrierUsage(),
    enabled: open && Boolean(sucursalId),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  })

  // Mapa carrier_id → count para O(1) lookup al ordenar
  const carrierUsageMap = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>()
    for (const entry of (carrierUsageData ?? [])) {
      m.set(entry.carrier_id, entry.count)
    }
    return m
  }, [carrierUsageData])

  const allProducts: WbsProduct[] = allProductsData ?? []

  // Productos filtrados por tipo seleccionado
  const productsByType = useMemo(() => {
    if (!selectedType) return []
    return selectedType === 'SERVICE'
      ? allProducts.filter(productIsService)
      : allProducts.filter(p => !productIsService(p))
  }, [allProducts, selectedType])

  // Carriers únicos del tipo seleccionado, ordenados por uso en la sucursal
  const carriers = useMemo(
    () => groupCarriers(productsByType, carrierUsageMap),
    [productsByType, carrierUsageMap]
  )

  // Carriers filtrados por búsqueda
  const filteredCarriers = useMemo(() => {
    if (!carrierSearch.trim()) return carriers
    const lower = carrierSearch.toLowerCase()
    return carriers.filter(c => c.name.toLowerCase().includes(lower))
  }, [carriers, carrierSearch])

  // Paquetes del carrier seleccionado
  const packagesForCarrier = useMemo(() => {
    if (!selectedCarrierId) return []
    const lower = packageSearch.toLowerCase()
    return productsByType
      .filter(p => p.carrier_id === selectedCarrierId)
      .filter(p => !lower || p.description.toLowerCase().includes(lower))
  }, [productsByType, selectedCarrierId, packageSearch])

  // Tipos disponibles (derivados del catálogo)
  const hasRecharges = useMemo(() => allProducts.some(p => !productIsService(p)), [allProducts])
  const hasServices = useMemo(() => allProducts.some(productIsService), [allProducts])

  const handleSelectProduct = (p: WbsProduct) => {
    setSelectedProduct(p)
    setDebtAmountEditable(null)
    if (!p.check_amount && p.amount !== '0') {
      setAmount(p.amount)
    } else {
      setAmount('')
    }
  }

  const handleResetSelector = () => {
    setSelectedType(null)
    setSelectedCarrierId(null)
    setSelectedProduct(null)
    setPackageSearch('')
    setCarrierSearch('')
    setAmount('')
    setDebtAmountEditable(null)
  }

  // Get Balance Mutation
  const checkBalanceMutation = useMutation({
    mutationFn: wbsService.getBalance,
    onSuccess: (res: any) => {
      const raw = res?.data
      setBalanceDetails(raw)
      toast.success('Saldo actualizado correctamente.')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Error al obtener el saldo.')
    }
  })

  // Check Debt Mutation
  const checkDebtMutation = useMutation({
    mutationFn: wbsService.checkDebt,
    onSuccess: (res: any) => {
      const data = res?.data;
      if (data?.response !== 1 || data?.rcode === 1) {
        toast.error(data?.message || 'El proveedor no pudo consultar el adeudo.')
        return
      }

      if (data.rcode === 2) {
        setDebtAmountEditable(true)
        setAmount('')
        toast.info(data.message || 'La referencia es válida. Captura el monto manualmente.')
        return
      }

      setAmount(String(data.amount ?? '0'))
      setDebtAmountEditable(Boolean(data.editable))
      toast.success(`Adeudo encontrado: $${Number(data.amount || 0).toFixed(2)}`)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Error al consultar el adeudo.')
    }
  })

  // Check Status Mutation (Polling)
  const checkStatusMutation = useMutation({
    mutationFn: wbsService.checkStatus,
    onSuccess: (res: any) => {
      addDebugLog('CHECK_STATUS_SUCCESS', res);
      const tx = res?.data?.transaction
      // txStatus is in tx.status
      if (
        tx?.status === 'PENDING' ||
        tx?.status === 'CONSULTING' ||
        tx?.status === 'PENDING_RESOLUTION' ||
        tx?.status === 'COMMUNICATION_ERROR' ||
        tx?.status === 'SENDING'
      ) {
        setPollCount(p => p + 1)
      } else {
        if (tx?.status === 'SUCCESS') {
          setManualTransactionResult(tx)
          setTxStatus('SUCCESS')
          toast.success('Recarga confirmada exitosamente.')
        } else {
          setTxStatus('FAILED')
          toast.error(tx?.wbs_description || 'La transacción falló o fue declinada.')
        }
      }
    },
    onError: (err: any) => {
      addDebugLog('CHECK_STATUS_ERROR', err?.response?.data || err?.message);
      // Si falla la red durante el polling, seguimos intentando hasta timeout
      setPollCount(p => p + 1)
      console.error('Error al consultar estado:', err)
    }
  })

  // Polling Effect
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    if (txStatus === 'PENDING' && activeFolio) {
      if (pollCount > 75) { // 75 intentos: 12s inicial + 74 * 4s = ~308s (>5 min)
        setTxStatus('FAILED')
        toast.error('La transacción está tardando más de lo normal. Verifica el historial en unos minutos.')
        return
      }

      // El primer intento espera 12s: WBS necesita tiempo para registrar la transacción
      // tras devolver código 24 (PENDING). Los reintentos posteriores son cada 4s.
      const delay = pollCount === 0 ? 12000 : 4000

      timeoutId = setTimeout(() => {
        checkStatusMutation.mutate(activeFolio)
      }, delay)
    }
    return () => clearTimeout(timeoutId)
  }, [txStatus, activeFolio, pollCount])

  // Get Transactions Query
  const { data: transactionsQuery, isFetching: isFetchingHistory, refetch: refetchTransactions } = useQuery({
    queryKey: ['wbs-transactions', debouncedHSearch, hPage, sucursalId],
    queryFn: () => wbsService.getTransactions({ search: debouncedHSearch, page: hPage, per_page: 8 }),
    enabled: open && activeTab === 'history' && Boolean(sucursalId),
    placeholderData: (prev: any) => prev,
  })

  // Initiate Transaction Mutation
  const initiateMutation = useMutation({
    mutationFn: wbsService.initiateTransaction,
    onSuccess: (res: any) => {
      addDebugLog('INITIATE_SUCCESS', res);
      const payload = res?.data
      const tx = payload?.transaction
      setManualTransactionResult(tx ?? null)
      const resolvedFolio = payload?.folio_pos || tx?.folio_pos || ''
      setActiveFolio(resolvedFolio)

      const isPending = payload?.status === 'PENDING' || tx?.status === 'PENDING' || tx?.status === 'SENDING'

      if (isPending) {
        setTxStatus('PENDING')
        setPollCount(0)
      } else {
        setTxStatus('SUCCESS')
        toast.success(`Recarga exitosa. Folio: ${tx?.wbs_folio || tx?.folio_pos || 'N/A'}`)
      }
    },
    onError: (err: any) => {
      addDebugLog('INITIATE_ERROR', err?.response?.data || err?.message);
      setTxStatus('FAILED')
      toast.error(err?.response?.data?.message || 'Error al iniciar la recarga.')
    }
  })

  const handleRecharge = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProduct || !amount || !phoneNumber) {
      toast.error('Por favor completa todos los campos para la recarga.')
      return
    }

    // Validación estricta de 10 dígitos numéricos para recargas
    if (!isService && !/^\d{10}$/.test(phoneNumber)) {
      toast.error('Número inválido. Debes ingresar un número de teléfono de exactamente 10 dígitos.');
      return;
    }
    setTxStatus('SENDING')
    initiateMutation.mutate({
      type: isService ? 'SERVICE_PAYMENT' : 'RECHARGE',
      carrier_id: selectedProduct.carrier_id,
      carrier_description: selectedProduct.description || selectedProduct.carrier_id,
      amount: amount,
      reference: phoneNumber,
      payment_method: 'cash',
    })
  }

  const handleReset = () => {
    setTxStatus('IDLE')
    setSelectedProduct(null)
    setSelectedType(null)
    setSelectedCarrierId(null)
    setPackageSearch('')
    setCarrierSearch('')
    setAmount('')
    setDebtAmountEditable(null)
    setPhoneNumber('')
    setPaymentMethod('cash')
    setActiveFolio('')
    setPollCount(0)
  }

  const handleCheckTransactionManual = (e: React.FormEvent) => {
    e.preventDefault()
    if (!folio) {
      setManualTransactionResult(null)
      setManualTransactionError({
        title: 'Folio requerido',
        message: 'Ingresa el folio de la operación para consultar su estado.',
      })
      return
    }

    setManualTransactionResult(null)
    setManualTransactionError(null)
    setIsSubmitting(true)
    wbsService.checkStatus(folio)
      .then((res) => {
        const tx = res?.data?.transaction
        if (!tx) throw new Error('La respuesta no contiene la transacción solicitada.')
        setManualTransactionResult(tx)
      })
      .catch((err: any) => {
        const status = Number(err?.response?.status || 0)
        setManualTransactionError(status === 404
          ? {
            title: 'Transacción no encontrada',
            message: 'No existe una transacción WBS con el folio capturado.',
          }
          : {
            title: 'Error de comunicación',
            message: 'No fue posible consultar la transacción. Intenta nuevamente en unos momentos.',
          })
      })
      .finally(() => setIsSubmitting(false))
  }

  // Quick amount presets (only shown when the product allows custom amount)
  const amountPresets = ['20', '50', '100', '200', '300', '500']

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] flex flex-col max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-100">
                <Zap className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <DialogTitle className="text-xl">Recargas y Servicios</DialogTitle>
                <DialogDescription>Gestión de saldo, tiempo aire y pagos.</DialogDescription>
              </div>
            </div>
            {/* Toggle Debug Mode */}
            {showDebugButton && (
              <button
                onClick={() => setIsDebugMode(!isDebugMode)}
                className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 self-end"
              >
                {isDebugMode ? 'Ocultar Debug' : 'Debug'}
              </button>
            )}
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4 flex flex-col flex-1 min-h-0">
          <TabsList className="grid w-full grid-cols-3 shrink-0">
            <TabsTrigger value="recharge">Recargas</TabsTrigger>
            <TabsTrigger value="transaction">Transacción</TabsTrigger>
            <TabsTrigger value="history">Historial</TabsTrigger>
            {/* Ocultamos saldo pero conservamos el trigger si se necesita */}
            <TabsTrigger value="balance" className="hidden">Saldo</TabsTrigger>
          </TabsList>

          {/* ── RECARGAS TAB ── */}
          <TabsContent value="recharge" className="flex-1 min-h-0 pt-4 pr-1 overflow-y-auto">
            <form onSubmit={handleRecharge} className="space-y-4 pb-2">

              {/* ── SELECTOR GUIADO 3 PASOS ────────────────────────────────── */}
              <div className="space-y-3">

                {/* Breadcrumb de pasos */}
                {(selectedType || selectedProduct) && (
                  <div className="flex items-center gap-1.5 text-xs text-card-description">
                    <button
                      type="button"
                      onClick={handleResetSelector}
                      className="text-orange-500 hover:text-orange-700 font-medium hover:underline"
                    >
                      {selectedType === 'RECHARGE' ? 'Recargas' : 'Servicios'}
                    </button>
                    {selectedCarrierId && (
                      <>
                        <span>/</span>
                        <button
                          type="button"
                          onClick={() => { setSelectedCarrierId(null); setSelectedProduct(null); setPackageSearch(''); setAmount('') }}
                          className="text-orange-500 hover:text-orange-700 font-medium hover:underline"
                        >
                          {carriers.find(c => c.carrier_id === selectedCarrierId)?.name || selectedCarrierId}
                        </button>
                      </>
                    )}
                    {selectedProduct && (
                      <>
                        <span>/</span>
                        <span className="text-inherit opacity-70 font-medium truncate max-w-[140px]">{selectedProduct.description}</span>
                      </>
                    )}
                  </div>
                )}

                {/* PASO 1: Tipo de transacción */}
                {!selectedType && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-inherit">Tipo de operación</label>
                    {isFetchingProducts ? (
                      <div className="flex items-center justify-center py-8 gap-2 text-card-description">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Cargando catálogo...</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                       {hasRecharges && (
                        <button
                          type="button"
                          onClick={() => setSelectedType('RECHARGE')}
                          className="flex flex-col items-center gap-2 rounded-xl border-2 border-border bg-card p-5 text-center hover:border-orange-400 hover:bg-primary/5 transition-all duration-150 group"
                        >
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                            <Zap className="h-6 w-6 text-orange-500" />
                          </div>

                          <span className="font-semibold text-card-foreground text-sm">
                            Recargas
                          </span>

                          <span className="text-xs text-card-description">
                            Tiempo aire y datos
                          </span>
                        </button>
                      )}
                       {hasServices && (
                        <button
                          type="button"
                          onClick={() => setSelectedType('SERVICE')}
                          className="flex flex-col items-center gap-2 rounded-xl border-2 border-border bg-card p-5 text-center hover:border-blue-400 hover:bg-primary/5 transition-all duration-150 group"
                        >
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                            <DollarSign className="h-6 w-6 text-blue-500" />
                          </div>

                          <span className="font-semibold text-card-foreground text-sm">
                            Servicios
                          </span>

                          <span className="text-xs text-card-description">
                            Pagos y servicios
                          </span>
                        </button>
                      )}
                      </div>
                    )}
                  </div>
                )}

                {/* PASO 2: Operador */}
                {selectedType && !selectedCarrierId && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-card-foreground">Selecciona el operador</label>
                      <button
                        type="button"
                        onClick={handleResetSelector}
                        className="flex items-center gap-1 text-xs text-card-description hover:text-orange-500 transition-colors"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Cambiar tipo
                      </button>
                    </div>
                    {/* Buscador de operador usando el componente global del sistema */}
                    {carriers.length > 6 && (
                      <SearchFilter
                        placeholder="Buscar operador..."
                        initialValue={carrierSearch}
                        debounceDelay={150}
                        onSearch={(val) => setCarrierSearch(val)}
                      />
                    )}
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-[50vh] overflow-y-auto pr-0.5">
                      {filteredCarriers.map((c) => (
                        <button
                          key={c.carrier_id}
                          type="button"
                          onClick={() => { setSelectedCarrierId(c.carrier_id); setCarrierSearch('') }}
                          className="flex flex-col items-center gap-1.5 rounded-lg border border-border bg-card p-3 text-center hover:border-orange-400 hover:bg-primary/5 transition-all duration-150 group"
                        >
                          <CarrierLogo carrierId={c.carrier_id} description={c.name} size={32} />
                          <span className="text-[11px] font-medium text-card-foreground leading-tight line-clamp-2">{c.name}</span>
                          <span className="text-[10px] text-card-description">{c.count} {c.count === 1 ? 'opción' : 'opciones'}</span>
                        </button>
                      ))}
                      {filteredCarriers.length === 0 && (
                        <p className="col-span-3 py-4 text-center text-xs text-card-description">Sin resultados para "{carrierSearch}".</p>
                      )}
                    </div>
                  </div>
                )}

                {/* PASO 3: Paquete / Producto */}
                {selectedType && selectedCarrierId && !selectedProduct && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-card-foreground">Selecciona el paquete</label>
                      <button
                        type="button"
                        onClick={() => { setSelectedCarrierId(null); setPackageSearch('') }}
                        className="flex items-center gap-1 text-xs text-card-description hover:text-orange-500 transition-colors"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Cambiar operador
                      </button>
                    </div>
                    {/* Buscador de paquetes */}
                    {packagesForCarrier.length > 6 && (
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-card-description" />
                        <input
                          type="text"
                          value={packageSearch}
                          onChange={(e) => setPackageSearch(e.target.value)}
                          placeholder="Buscar paquete..."
                          className="w-full rounded-md border border-border bg-card py-1.5 pl-8 pr-3 text-xs outline-none focus:border-orange-400"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto pr-0.5">
                      {packagesForCarrier.map((p, i) => (
                        <button
                          key={`${p.carrier_id}-${p.amount}-${i}`}
                          type="button"
                          onClick={() => handleSelectProduct(p)}
                          className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:border-orange-400 hover:bg-primary/5 transition-all duration-150"
                        >
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-card-foreground truncate">{p.description}</span>
                            {p.note && (
                              <span className="text-[10px] text-card-description truncate">{p.note}</span>
                            )}
                          </div>
                          <span className="ml-3 shrink-0 rounded-full bg-orange-500/10 px-2.5 py-1 text-xs font-bold text-orange-500">
                            {Number(p.amount) === 0 ? 'Libre' : `$${p.amount}`}
                          </span>
                        </button>
                      ))}
                      {packagesForCarrier.length === 0 && (
                        <p className="py-4 text-center text-xs text-card-description">No hay paquetes disponibles.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Producto seleccionado — resumen */}
                {selectedProduct && (
                  <button title="Cambiar paquete" onClick={() => { setSelectedProduct(null); setAmount(''); setPackageSearch('') }} className="flex w-full items-center justify-between rounded-xl border-2 border-orange-400 bg-card px-4 py-3 cursor-pointer hover:bg-primary/5 transition-colors">
                    <div className="flex items-center gap-3">
                      <CarrierLogo carrierId={selectedProduct.carrier_id} description={selectedProduct.description} size={32} />
                      <div>
                        <p className="text-sm font-semibold text-card-foreground">{selectedProduct.description}</p>
                        <p className="text-xs text-card-description">{selectedProduct.group}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-orange-200 px-3 py-1 text-sm font-bold text-orange-800">
                        {Number(selectedProduct.amount) === 0 ? 'Monto libre' : `$${selectedProduct.amount}`}
                      </span>
                      <ChevronLeft className="h-4 w-4" />
                    </div>
                  </button>
                )}

                {/* Nota del producto */}
                
              {selectedProduct?.note && (
                <div className="flex gap-2 rounded-md border border-border bg-card p-3 text-xs text-card-description">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-400" />
                  <p className="leading-relaxed">{selectedProduct.note}</p>
                </div>
              )}
              </div>
              {/* ── FIN SELECTOR ─────────────────────────────────────────────── */}

              {/* Los campos de monto, teléfono y pago solo aparecen tras elegir el paquete */}
              {selectedProduct && (<>

                {/* Amount */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-inherit">Monto</label>
                  <div className="flex flex-col gap-2">
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inherit opacity-70" />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Ej. 100.00"
                        className="pl-9 font-medium"
                        value={amount}
                        readOnly={isAmountFixed}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                    {/* Quick presets — only if product allows custom amount or nothing selected */}
                    {(!selectedProduct || !isAmountFixed) && (
                      <div className="flex flex-wrap gap-2">
                        {amountPresets.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setAmount(preset)}
                            className={`rounded-md border px-3 py-1 text-xs font-medium transition-colors ${amount === preset
                              ? 'bg-orange-100 border-orange-200 text-orange-700'
                              : 'bg-card border-border text-inherit opacity-70 hover:bg-primary/5'
                              }`}
                          >
                            ${preset}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone / Referencia */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-inherit">
                    {isService ? 'Referencia / Número de cuenta' : 'Número de Teléfono (10 dígitos)'}
                  </label>
                  <div className="relative">
                    <Smartphone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                    <Input
                      type="text"
                      placeholder={isService ? 'Ej. 01234567890' : 'Ej. 5512345678'}
                      className="pl-9"
                      value={phoneNumber}
                      maxLength={isService ? undefined : 10}
                      onChange={(e) => {
                        const val = e.target.value
                        if (!isService) {
                          setPhoneNumber(val.replace(/\D/g, ''))
                        } else {
                          setPhoneNumber(val)
                          if (selectedProduct?.check_amount) {
                            setAmount('')
                            setDebtAmountEditable(null)
                          }
                        }
                      }}
                    />
                  </div>
                  {/* Nota informativa para servicios: el monto se captura manualmente */}
                  {isService && (
                    <p className="text-xs text-card-description leading-relaxed">
                      Ingresa la referencia de contrato o número de cuenta del servicio.
                    </p>
                  )}
                  {canCheckWbsDebt(isService, selectedProduct, phoneNumber) && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      disabled={checkDebtMutation.isPending}
                      onClick={() => checkDebtMutation.mutate({
                        carrier_id: selectedProduct.carrier_id,
                        reference: phoneNumber.trim(),
                      })}
                    >
                      {checkDebtMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Consultar adeudo
                    </Button>
                  )}
                </div>

                {isService && Number(amount || 0) >= 0 && ( 
                  <div className="rounded-md border border-orange-500/30 bg-card p-3 text-sm"> 
                    <div className="flex justify-between text-card-description"> 
                      <span>Monto del servicio</span> 
                      <span className="text-card-foreground">
                        ${Number(amount || 0).toFixed(2)}
                      </span> 
                    </div> 

                    <div className="mt-1 flex justify-between text-card-description"> 
                      <span>Comisión</span> 
                      <span className="text-card-foreground">
                        ${serviceCommission.toFixed(2)}
                      </span> 
                    </div> 

                    <div className="mt-2 flex justify-between border-t border-orange-500/30 pt-2 font-semibold text-orange-500"> 
                      <span>Total a cobrar</span> 
                      <span>${totalToCharge.toFixed(2)}</span> 
                    </div> 
                  </div> 
                )}
                {isService && isCheckoutConfigError && (
                  <p className="text-xs text-destructive">No se pudo obtener la comisión configurada. Cierra y vuelve a abrir el modal.</p>
                )}

                {/* Método de Pago — siempre efectivo, oculto */}
                {/* payment_method se envía hardcodeado como 'cash' en handleRecharge */}

                {/* ── FIN CONDITIONAL PRODUCTO ── */}
              </>
              )}

              {/* El bloque de estados siempre es visible para mostrar SENDING/PENDING/SUCCESS/FAILED */}
              <div className="pt-2">
                {txStatus === 'IDLE' ? (
                  selectedProduct ? (
                    <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700" disabled={initiateMutation.isPending || (isService && (!checkoutConfig || isCheckoutConfigError))}>
                      {initiateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                      Confirmar {isService ? 'Pago' : 'Recarga'}
                    </Button>
                  ) : null
                ) : txStatus === 'SENDING' ? (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-orange-50 border border-orange-100">
                    <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                    <p className="text-sm text-orange-800 font-medium">Iniciando transacción...</p>
                  </div>
                ) : txStatus === 'PENDING' ? (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-blue-50 border border-blue-100">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    <p className="text-sm text-blue-800 font-medium">Confirmando transacción con el proveedor...</p>
                    <p className="text-xs text-blue-600">Esto puede tomar hasta 5 minutos. ({pollCount}/75)</p>
                  </div>
                ) : txStatus === 'SUCCESS' ? (
                  <div className="flex flex-col items-center justify-center space-y-4 p-4 rounded-md bg-emerald-50 border border-emerald-100">
                    <div className="text-emerald-600 font-medium text-sm text-center">¡Transacción Exitosa!</div>
                    <Button
                      type="button"
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={checkoutMutation.isPending || !registerContext?.cash_register?.id}
                      onClick={() => {
                        checkoutMutation.mutate({
                          folio: activeFolio,
                          payment_method_id: 1, // Efectivo por defecto
                        })
                      }}
                    >
                      {checkoutMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />}
                      Registrar Venta e Imprimir Ticket
                    </Button>
                    <Button type="button" variant="outline" className="w-full mt-2" onClick={handleReset}>
                      Nueva Recarga
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-2 p-4 rounded-md bg-destructive/10 border border-red-100">
                    <div className="text-destructive font-medium text-sm text-center">La recarga falló.</div>
                    <Button type="button" variant="outline" className="w-full mt-2" onClick={handleReset}>
                      Intentar Nuevamente
                    </Button>
                  </div>
                )}
              </div>
            </form>

            {isDebugMode && (
              <div className="mt-4 p-4 bg-slate-900 text-green-400 font-mono text-xs rounded overflow-y-auto max-h-48">
                <h4 className="text-white mb-2 font-bold">Modo Depuración</h4>
                <div className="mb-2 text-slate-300">
                  Estado UI: {txStatus} | Folio Activo: {activeFolio || 'Ninguno'} | Polling: {pollCount}
                </div>
                {debugLogs.map((log, i) => (
                  <div key={i} className="mb-2 border-b border-slate-800 pb-2">
                    <span className="text-blue-300">[{new Date(log.time).toLocaleTimeString()}]</span> <strong className="text-yellow-300">{log.type}</strong>
                    <pre className="mt-1 whitespace-pre-wrap break-all">{JSON.stringify(log.data, null, 2)}</pre>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── TRANSACCIÓN TAB ── */}
          <TabsContent value="transaction" className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1">
            <form onSubmit={handleCheckTransactionManual} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-card-foreground">Folio de Operación</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Ej. 10008110201031234"
                    className="pl-9"
                    value={folio}
                    onChange={(e) => {
                      setFolio(e.target.value)
                      setManualTransactionResult(null)
                      setManualTransactionError(null)
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500">Ingresa el folio para verificar si la recarga fue exitosa.</p>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Verificar Transacción
              </Button>
            </form>

            {manualTransactionError && (
              <div className="mt-4">
                <WbsTransactionLookupErrorCard {...manualTransactionError} />
              </div>
            )}

            {manualTransactionResult && (
              <div className="mt-4">
                <WbsTransactionResultCard
                  transaction={manualTransactionResult}
                  canComplete={Boolean(registerContext?.cash_register?.id)}
                  isCompleting={checkoutMutation.isPending}
                  onComplete={() => checkoutMutation.mutate({
                    folio: manualTransactionResult.folio_pos,
                    payment_method_id: 1,
                  })}
                />
              </div>
            )}
          </TabsContent>

          {/* ── SALDO TAB ── */}
          <TabsContent value="balance" className="pt-4">
            <div className="flex flex-col items-center space-y-4 rounded-lg border border-table-border bg-card p-6">
              <div className="rounded-full bg-emerald-500/15 p-3">
                <DollarSign className="h-6 w-6 text-emerald-400" />
              </div>
              <div className="text-center w-full">
                <p className="text-sm font-medium text-card-foreground mb-1">Saldo Disponible</p>
                <h3 className={`font-bold ${balanceDetails?.Balance ? 'text-4xl text-emerald-400' : 'text-xl text-card-description'}`}>
                  {balanceDetails?.Balance ? `$${balanceDetails.Balance} MXN` : '---'}
                </h3>
              </div>

              {balanceDetails && (
                <div className="w-full rounded-md border border-slate-200 bg-white divide-y divide-slate-100 text-sm overflow-hidden">
                  {[
                    { label: 'Saldo Inicial', value: balanceDetails.Saldo_Inicial },
                    { label: 'Compras', value: balanceDetails.Compras },
                    { label: 'Ventas', value: balanceDetails.Ventas },
                    { label: 'Comisión', value: balanceDetails.Comision },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between px-3 py-2">
                      <span className="text-slate-500">{label}</span>
                      <span className="font-medium text-card-foreground">${value ?? '0'}</span>
                    </div>
                  ))}
                  {balanceDetails.Confirmation && (
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50">
                      <span className="text-xs text-card-description">Confirmación</span>
                      <span className="text-xs font-mono text-slate-500">{balanceDetails.Confirmation}</span>
                    </div>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => checkBalanceMutation.mutate()}
                disabled={checkBalanceMutation.isPending}
                className="w-full border-primary/50 bg-primary text-card-foreground hover:bg-primary/40 hover:text-card-foreground"
              >
                {checkBalanceMutation.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : 'Consultar Saldo Actual'}
              </Button>
            </div>
          </TabsContent>

          {/* ── HISTORIAL TAB ── */}
          <TabsContent value="history" className="flex-1 min-h-0 overflow-y-auto pt-4 pr-1">
            <div className="flex flex-col gap-3 pb-2">

              {/* Barra de búsqueda + actualizar */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex-1">
                  <SearchFilter
                    placeholder="Buscar por folio o referencia..."
                    initialValue={hSearch}
                    onSearch={(val) => {
                      setHSearch(val)
                      setHPage(1)
                    }}
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 shrink-0"
                  onClick={() => {
                    if (hPage === 1) {
                      refetchTransactions()
                    } else {
                      setHPage(1)
                    }
                  }}
                  disabled={isFetchingHistory}
                >
                  {isFetchingHistory ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  Actualizar
                </Button>
              </div>

              {/* Lista de transacciones */}
              <div className="flex flex-col gap-1.5">
                {isFetchingHistory && !transactionsQuery?.data?.data?.length ? (
                  <div className="flex flex-col items-center justify-center h-32 gap-2 text-card-description rounded-lg border border-border bg-card">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-sm">Cargando historial...</span>
                  </div>
                ) : !transactionsQuery?.data?.data?.length ? (
                  <div className="flex items-center justify-center h-32 rounded-lg border border-border bg-card text-sm text-card-description">
                    No hay transacciones registradas.
                  </div>
                ) : (
                  transactionsQuery.data.data.map((tx: any) => {
                    const isFailed = tx.status !== 'SUCCESS' && tx.status !== 'PENDING'
                    const isPending = tx.status === 'PENDING'
                    const isSuccess = tx.status === 'SUCCESS'

                    return (
                      <div
                        key={tx.id}
                        className={`
                          flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-xs transition-colors
                          ${isFailed ? 'bg-red-500/10 border-red-500/40' : ''}
                          ${isPending ? 'bg-blue-500/10 border-blue-500/40' : ''}
                          ${isSuccess ? 'bg-card border-emerald-500/40' : ''}
                        `}
                      >
                        {/* Indicador de color lateral */}
                        <span className={`w-1 self-stretch rounded-full shrink-0 ${isFailed ? 'bg-red-400' :
                          isPending ? 'bg-blue-400' :
                            'bg-emerald-400'
                          }`} />

                        {/* Info principal */}
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold truncate ${isFailed ? 'text-red-500' : 'text-card-foreground'}`}>
                              {tx.carrier_description || tx.carrier_id}
                            </span>
                            <span className="text-card-description shrink-0">·</span>
                            <span className={`truncate ${isFailed ? 'text-red-400' : 'text-card-description'}`}>
                              Ref. {tx.reference}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-card-description">
                            <span>{new Date(tx.created_at).toLocaleString('es-MX', {
                              day: '2-digit', month: '2-digit', year: '2-digit',
                              hour: '2-digit', minute: '2-digit'
                            })}</span>
                            <span>·</span>
                            <span className="font-mono truncate">
                              {tx.folio_pos || tx.wbs_folio || '—'}
                            </span>
                          </div>
                        </div>

                        {/* Monto + Badge */}
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`font-bold text-sm ${isFailed ? 'text-red-500' : 'text-card-foreground'}`}>
                            ${tx.amount}
                          </span>
                          {isSuccess ? (
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                Exitoso
                              </Badge>
                              {!tx.sale_id ? (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700 mt-1"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    checkoutMutation.mutate({
                                      folio: tx.folio_pos || tx.wbs_folio,
                                      payment_method_id: 1
                                    })
                                  }}
                                  disabled={checkoutMutation.isPending || !registerContext?.cash_register?.id}
                                >
                                  Completar Venta
                                </Button>
                              ) : (
                                <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-primary/10 text-card-description border-border mt-1">
                                  Venta #{tx.sale_id}
                                </Badge>
                              )}
                            </div>
                          ) : isPending ? (
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-blue-500/10 text-blue-400 border-blue-500/30">
                              Pendiente
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-red-500/10 text-red-400 border-red-500/30 font-semibold">
                              Fallido
                            </Badge>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Paginación */}
              {transactionsQuery?.data && (
                <GlobalPaginator
                  totalItems={transactionsQuery.data.total}
                  currentPage={transactionsQuery.data.current_page}
                  itemsPerPage={transactionsQuery.data.per_page}
                  from={(transactionsQuery.data.current_page - 1) * transactionsQuery.data.per_page + 1}
                  to={Math.min(transactionsQuery.data.current_page * transactionsQuery.data.per_page, transactionsQuery.data.total)}
                  onPageChange={(page) => setHPage(page)}
                />
              )}
            </div>
          </TabsContent>

        </Tabs>
      </DialogContent>
      {showTicketPreview && generatedSale && (
        <TicketPreviewModal
          open={showTicketPreview}
          onOpenChange={setShowTicketPreview}
          ticketNumber={generatedSale.ticket_number || ''}
          branchName={registerContext?.sucursal?.name || ''}
          cashRegisterName={registerContext?.cash_register?.name || ''}
          cashierName={[
            registerContext?.seller?.name,
            registerContext?.seller?.last_name,
          ].filter(Boolean).join(' ')}
          customerType="general"
          selectedCustomer={null}
          customers={[]}
          saleType="regular"
          deliveryDetails={{} as any}
          cart={generatedSale.items?.map((i: any) => ({
            id: i.id,
            productId: i.product_id || 0,
            name: i.product_name,
            sku: i.product_sku || '',
            barcode: i.product_barcode || '',
            quantity: Number(i.quantity),
            price: Number(i.unit_price_amount),
            cost: Number(i.unit_cost_amount || 0),
            type: 'general',
            discountAmount: 0,
            requiresPrescription: false,
            stock: 0
          })) || []}
          subtotal={generatedSaleAmounts.subtotal}
          tax={generatedSaleAmounts.tax}
          deliveryCost={0}
          pointsDiscount={0}
          total={generatedSaleAmounts.total}
          pointsEarned={0}
          selectedPaymentMethod="cash"
          selectedBank={''}
          reference={''}

          wbsType={manualTransactionResult?.type}
          wbsReference={manualTransactionResult?.reference}
          wbsCarrier={manualTransactionResult?.carrier_description}

          cashReceived={
            generatedSale.payments?.[0]?.cash_received_amount ||
            generatedSale.amounts?.total ||
            generatedSale.total_amount ||
            '0'
          }
          change={Number(generatedSale.payments?.[0]?.change_amount || 0)}
          requiresInvoice={false}
          ticketCopies={1}
          completeSale={() => {
            setShowTicketPreview(false)
            return true
          }}
          setShowPaymentModal={() => { }}
        />
      )}
    </Dialog>
  )
}
